import { z } from "zod";
/**
 * Permission bits from JSON or an install URL. Bit 31 (Share screen) is the int32
 * sign bit, so accept both the signed form and its unsigned form (as URLs carry it)
 * and normalise to int32, which is how roles store them.
 */
const permissionField = z.coerce.number().int().min(-2147483648).max(4294967295).transform((n) => n | 0);
import type { CommunitySummary, UserSummary } from "./types";
import type { CommandDefinition, CommandOption } from "./rich";

/*
 * The developer platform contract, shared by the server, the developer portal,
 * the documentation site and the SDK. Anything a third party can depend on
 * (scopes, intents, event names, rate limits, error codes) is defined here once.
 */

export const API_VERSION = 1;

/* ───────────── OAuth2 scopes ───────────── */

export const OAUTH_SCOPES = ["identify", "email", "communities", "bot"] as const;
export type OAuthScope = (typeof OAUTH_SCOPES)[number];

export const OAUTH_SCOPE_INFO: Record<OAuthScope, { label: string; description: string }> = {
  identify: { label: "Know who you are", description: "Your username, display name, avatar and account ID." },
  email: { label: "See your email address", description: "The email address on your account." },
  communities: { label: "See your communities", description: "The communities you're a member of." },
  bot: { label: "Add a bot to a community", description: "Adds the app's bot to a community you manage." },
};

/* ───────────── API key scopes ───────────── */

export const API_KEY_SCOPES = ["applications.read", "applications.commands", "webhooks.read", "webhooks.write", "analytics.read"] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export const API_KEY_SCOPE_INFO: Record<ApiKeyScope, string> = {
  "applications.read": "Read the application's configuration.",
  "applications.commands": "Create, edit and delete slash commands.",
  "webhooks.read": "List webhooks and their recent deliveries.",
  "webhooks.write": "Register, update and delete webhooks.",
  "analytics.read": "Read API usage analytics.",
};

/* ───────────── Gateway intents ───────────── */

/**
 * Intents decide which events a bot can receive. Bits are append-only.
 * Privileged intents expose more personal data and are off by default.
 */
export const Intent = {
  COMMUNITIES: 1 << 0,
  COMMUNITY_MEMBERS: 1 << 1,
  COMMUNITY_MESSAGES: 1 << 2,
  MESSAGE_REACTIONS: 1 << 3,
  MESSAGE_CONTENT: 1 << 4,
} as const;
export type IntentKey = keyof typeof Intent;

export const INTENT_INFO: { key: IntentKey; label: string; description: string; privileged: boolean }[] = [
  { key: "COMMUNITIES", label: "Communities", description: "Being added to and removed from communities (BOT_INSTALLED, BOT_REMOVED).", privileged: false },
  { key: "COMMUNITY_MESSAGES", label: "Messages", description: "Messages created, edited and deleted in channels the bot can see.", privileged: false },
  { key: "MESSAGE_REACTIONS", label: "Reactions", description: "Reactions added to and removed from messages.", privileged: false },
  { key: "COMMUNITY_MEMBERS", label: "Members", description: "Members joining and leaving communities.", privileged: true },
  { key: "MESSAGE_CONTENT", label: "Message content", description: "Message text in events and API responses. Without it, content is empty unless the bot is mentioned.", privileged: true },
];

export const ALL_INTENTS = Object.values(Intent).reduce((a, b) => a | b, 0);
export const DEFAULT_INTENTS = Intent.COMMUNITIES | Intent.COMMUNITY_MESSAGES;

/* ───────────── Webhook events ───────────── */

export const WEBHOOK_EVENTS = [
  "BOT_INSTALLED",
  "BOT_REMOVED",
  "APPLICATION_AUTHORIZED",
  "APPLICATION_DEAUTHORIZED",
  "MESSAGE_CREATE",
  "MESSAGE_UPDATE",
  "MESSAGE_DELETE",
  "REACTION_ADD",
  "REACTION_REMOVE",
  "MEMBER_JOIN",
  "MEMBER_LEAVE",
  "INTERACTION_CREATE",
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const WEBHOOK_EVENT_INFO: Record<WebhookEvent, { description: string; intent: IntentKey | null }> = {
  BOT_INSTALLED: { description: "The bot was added to a community.", intent: "COMMUNITIES" },
  BOT_REMOVED: { description: "The bot was removed from a community.", intent: "COMMUNITIES" },
  APPLICATION_AUTHORIZED: { description: "A user authorised the application with OAuth2.", intent: null },
  APPLICATION_DEAUTHORIZED: { description: "A user revoked the application's access.", intent: null },
  MESSAGE_CREATE: { description: "A message was sent in a channel the bot can see.", intent: "COMMUNITY_MESSAGES" },
  MESSAGE_UPDATE: { description: "A message was edited.", intent: "COMMUNITY_MESSAGES" },
  MESSAGE_DELETE: { description: "A message was deleted.", intent: "COMMUNITY_MESSAGES" },
  REACTION_ADD: { description: "A reaction was added to a message.", intent: "MESSAGE_REACTIONS" },
  REACTION_REMOVE: { description: "A reaction was removed from a message.", intent: "MESSAGE_REACTIONS" },
  MEMBER_JOIN: { description: "Someone joined a community the bot is in.", intent: "COMMUNITY_MEMBERS" },
  MEMBER_LEAVE: { description: "Someone left or was removed from a community the bot is in.", intent: "COMMUNITY_MEMBERS" },
  INTERACTION_CREATE: { description: "Someone used one of the application's slash commands.", intent: null },
};

/**
 * Headers on every webhook request. Signature: `t=<unix seconds>,v1=<hex>` where
 * v1 = HMAC-SHA256(secret, `${t}.${body}`). The timestamp header repeats `t`.
 */
export const WEBHOOK_HEADERS = {
  signature: "X-Atrium-Signature",
  event: "X-Atrium-Event",
  delivery: "X-Atrium-Delivery",
  timestamp: "X-Atrium-Timestamp",
} as const;
/** Receivers should reject signatures older than this. */
export const WEBHOOK_TOLERANCE_SECONDS = 300;
/** Attempts per delivery. Retries back off exponentially: about 10s, 40s, 3m, 11m, 43m, 3h, then 6h. */
export const WEBHOOK_MAX_ATTEMPTS = 8;
/** A webhook is switched off after this many deliveries in a row fail every attempt. */
export const WEBHOOK_DISABLE_AFTER = 20;

/* ───────────── Credentials ───────────── */

/** Prefixes make leaked credentials easy to recognise and scan for. */
export const TOKEN_PREFIX = { bot: "atb_", access: "ato_", refresh: "atr_", apiKey: "atk_", clientSecret: "acs_", webhookSecret: "whsec_", interaction: "itk_" } as const;
export const ACCESS_TOKEN_TTL_SECONDS = 3600;
export const REFRESH_TOKEN_TTL_DAYS = 30;
export const AUTH_CODE_TTL_SECONDS = 300;
export const INTERACTION_TOKEN_TTL_MINUTES = 15;

/* ───────────── Rate limits ───────────── */

export interface RateLimitRule {
  bucket: string;
  description: string;
  limit: number;
  windowSeconds: number;
  per: "credential" | "credential + channel" | "client" | "ip";
}

export const API_RATE_LIMITS: RateLimitRule[] = [
  { bucket: "global", description: "Every authenticated request", limit: 1200, windowSeconds: 60, per: "credential" },
  { bucket: "unauthenticated", description: "Requests without credentials", limit: 60, windowSeconds: 60, per: "ip" },
  { bucket: "messages.create", description: "Sending messages", limit: 5, windowSeconds: 5, per: "credential + channel" },
  { bucket: "messages.modify", description: "Editing and deleting messages", limit: 10, windowSeconds: 10, per: "credential + channel" },
  { bucket: "reactions", description: "Adding and removing reactions", limit: 10, windowSeconds: 5, per: "credential + channel" },
  { bucket: "interactions.respond", description: "Responding to interactions", limit: 10, windowSeconds: 5, per: "credential" },
  { bucket: "commands.write", description: "Creating and updating commands", limit: 50, windowSeconds: 60, per: "credential" },
  { bucket: "webhooks.write", description: "Managing webhooks", limit: 30, windowSeconds: 60, per: "credential" },
  { bucket: "oauth2.token", description: "Token exchange and refresh", limit: 30, windowSeconds: 60, per: "client" },
];

/** Outgoing webhook traffic, per webhook endpoint. */
export const WEBHOOK_RATE_LIMIT = { limit: 50, windowSeconds: 10 };

/** Response headers on every rate-limited endpoint. */
export const RATE_LIMIT_HEADERS = {
  limit: "X-RateLimit-Limit",
  remaining: "X-RateLimit-Remaining",
  reset: "X-RateLimit-Reset",
  resetAfter: "X-RateLimit-Reset-After",
  bucket: "X-RateLimit-Bucket",
} as const;

/* ───────────── Errors ───────────── */

/** Machine-readable error codes returned by the public API. */
export const API_ERROR_CODES = {
  bad_request: "The request was malformed or failed validation.",
  unauthorized: "Credentials are missing, invalid, expired or revoked.",
  missing_scope: "The credential is valid but lacks a required scope.",
  missing_intent: "The bot hasn't enabled the intent this needs.",
  missing_permission: "The bot lacks a community or channel permission.",
  not_found: "The resource doesn't exist or isn't visible to this credential.",
  conflict: "The request conflicts with the current state.",
  rate_limited: "Too many requests. Wait for the time in Retry-After.",
  unsupported_version: "The API version in the path isn't supported.",
  interaction_expired: "The interaction token has expired or was already used.",
  internal: "Something went wrong on our side.",
} as const;
export type ApiErrorCode = keyof typeof API_ERROR_CODES;

/** Shape of every public API error response. OAuth2 token endpoints follow RFC 6749 instead. */
export interface ApiErrorBody {
  error: { code: ApiErrorCode; message: string; status: number; requestId: string; fields?: Record<string, string> };
}

/* ───────────── Slash commands ───────────── */

/* Command and option schemas are shared with the built-in commands; see rich.ts. */
export type CommandInput = CommandDefinition;
export const MAX_COMMANDS = 50;

export interface AppCommand {
  id: string;
  applicationId: string;
  /** Null for global commands (every community the app is installed in). */
  communityId: string | null;
  name: string;
  description: string;
  category: string;
  options: CommandOption[];
  /** Community permission bits needed to see and use the command (0 = everyone). */
  requiredPermissions: number;
  cooldownSeconds: number;
  createdAt: string;
  updatedAt: string;
}

/** Interaction response types accepted by the callback endpoint. */
export const InteractionResponseType = {
  /** Post a message in the channel, attributed to the command. */
  CHANNEL_MESSAGE: 4,
  /** Acknowledge now and post a follow-up message later (within the token lifetime). */
  DEFERRED_CHANNEL_MESSAGE: 5,
} as const;

/* ───────────── Portal inputs ───────────── */

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .url("Enter a valid URL")
  .refine((u) => /^https?:\/\//i.test(u), "Use an http(s) URL");

export const redirectUriSchema = z
  .string()
  .trim()
  .max(500)
  .refine((u) => {
    try {
      const url = new URL(u);
      // Custom schemes are allowed for native apps; fragments never are (RFC 6749 §3.1.2).
      return !url.hash && !/^(javascript|data|vbscript|file):$/i.test(url.protocol);
    } catch {
      return false;
    }
  }, "Enter an absolute URL without a fragment");

export const createApplicationSchema = z.object({
  name: z.string().trim().min(2, "At least 2 characters").max(40),
  description: z.string().trim().max(400).default(""),
});

export const updateApplicationSchema = z.object({
  name: z.string().trim().min(2, "At least 2 characters").max(40).optional(),
  description: z.string().trim().max(400).optional(),
  iconMediaId: z.string().uuid().nullable().optional(),
  redirectUris: z.array(redirectUriSchema).max(10).optional(),
  privacyPolicyUrl: z.union([httpsUrl, z.literal("")]).optional(),
  termsUrl: z.union([httpsUrl, z.literal("")]).optional(),
});

export const updateBotSchema = z.object({
  username: z
    .string()
    .trim()
    .min(2, "At least 2 characters")
    .max(24, "At most 24 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Letters, numbers and underscores only")
    .optional(),
  displayName: z.string().trim().min(1).max(50).optional(),
  avatarMediaId: z.string().uuid().nullable().optional(),
  public: z.boolean().optional(),
  intents: z.number().int().min(0).max(ALL_INTENTS).optional(),
  defaultPermissions: permissionField.optional(),
});

export const createApiKeySchema = z.object({
  name: z.string().trim().min(1, "Required").max(60),
  scopes: z.array(z.enum(API_KEY_SCOPES)).min(1, "Pick at least one scope").max(API_KEY_SCOPES.length),
  /** Days until expiry; null for no expiry. */
  expiresInDays: z.number().int().min(1).max(365).nullable().default(90),
});

export const webhookUrlSchema = httpsUrl;
export const createWebhookSchema = z.object({
  url: webhookUrlSchema,
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, "Pick at least one event").max(WEBHOOK_EVENTS.length),
  description: z.string().trim().max(100).default(""),
});
export const updateWebhookSchema = z.object({
  url: webhookUrlSchema.optional(),
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1).max(WEBHOOK_EVENTS.length).optional(),
  description: z.string().trim().max(100).optional(),
  active: z.boolean().optional(),
});

export const transferApplicationSchema = z.object({
  /** Username of the new owner. */
  username: z.string().trim().min(1).max(24),
  /** Must equal the application's current name, as a deliberate confirmation. */
  confirmName: z.string(),
});

/* ───────────── Portal DTOs ───────────── */

export interface DevBot {
  userId: string;
  publicId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  /** Anyone can install the bot. When off, only the owner can. */
  public: boolean;
  intents: number;
  /** Permissions pre-selected in generated install links. */
  defaultPermissions: number;
  /** Null until a token has been generated. The token itself is never returned again. */
  tokenCreatedAt: string | null;
  tokenLastUsedAt: string | null;
}

export interface DevApplication {
  id: string;
  /** The OAuth2 client ID. Same as `id`. */
  clientId: string;
  name: string;
  description: string;
  iconUrl: string | null;
  owner: UserSummary;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
  redirectUris: string[];
  privacyPolicyUrl: string;
  termsUrl: string;
  /** Last four characters of the client secret, when one exists. */
  clientSecretHint: string | null;
  clientSecretCreatedAt: string | null;
  bot: DevBot | null;
  installCount: number;
  authorizedUserCount: number;
}

export interface DevApplicationSummary {
  id: string;
  name: string;
  description: string;
  iconUrl: string | null;
  createdAt: string;
  hasBot: boolean;
  botAvatarUrl: string | null;
  installCount: number;
  requests24h: number;
}

/** Returned exactly once, when a secret is created or regenerated. */
export interface RevealedSecret {
  secret: string;
  createdAt: string;
}

export interface DevApiKey {
  id: string;
  name: string;
  /** First characters of the key, for recognising it. */
  prefix: string;
  scopes: ApiKeyScope[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface DevApiKeyCreated extends DevApiKey {
  key: string;
}

export interface DevWebhook {
  id: string;
  url: string;
  description: string;
  events: WebhookEvent[];
  active: boolean;
  /** Set when deliveries kept failing and the webhook was switched off automatically. */
  disabledReason: string | null;
  consecutiveFailures: number;
  secretHint: string;
  createdAt: string;
  lastDeliveryAt: string | null;
  lastSuccessAt: string | null;
}

export interface DevWebhookCreated extends DevWebhook {
  secret: string;
}

export type DeliveryStatus = "pending" | "succeeded" | "failed";

export interface DevWebhookDelivery {
  id: string;
  webhookId: string;
  event: WebhookEvent | "PING";
  status: DeliveryStatus;
  attempts: number;
  responseStatus: number | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string;
  nextAttemptAt: string | null;
  completedAt: string | null;
  /** Included when fetching a single delivery. */
  payload?: unknown;
}

export type CredentialType = "bot" | "oauth" | "api_key" | "none";

export interface DevRequestLog {
  id: string;
  requestId: string;
  method: string;
  route: string;
  path: string;
  status: number;
  durationMs: number;
  credentialType: CredentialType;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface DevAuditEntry {
  id: string;
  action: DevAuditAction;
  actor: UserSummary | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export type DevAuditAction =
  | "application.create"
  | "application.update"
  | "application.delete"
  | "application.transfer"
  | "oauth.secret.reset"
  | "bot.create"
  | "bot.update"
  | "bot.token.reset"
  | "bot.install"
  | "bot.remove"
  | "api_key.create"
  | "api_key.revoke"
  | "webhook.create"
  | "webhook.update"
  | "webhook.delete"
  | "webhook.secret.rotate"
  | "webhook.disabled";

export interface DevAnalytics {
  days: number;
  totals: { requests: number; errors: number; rateLimited: number; avgDurationMs: number; webhookDeliveries: number; webhookFailures: number };
  daily: { date: string; requests: number; errors: number; rateLimited: number }[];
  routes: { method: string; route: string; requests: number; errors: number; avgDurationMs: number }[];
  statuses: { status: number; count: number }[];
  credentials: { type: CredentialType; count: number }[];
}

export interface DevRateLimits {
  rules: RateLimitRule[];
  webhook: typeof WEBHOOK_RATE_LIMIT;
  /** Requests in the last hour that were rejected with 429, per bucket. */
  recentRejections: { bucket: string; count: number }[];
}

export interface DevInstallation {
  community: Pick<CommunitySummary, "id" | "name" | "slug" | "iconUrl" | "memberCount">;
  permissions: number;
  installedBy: UserSummary | null;
  installedAt: string;
}

export interface DevOverview {
  applications: DevApplicationSummary[];
  totals: { requests24h: number; errors24h: number; installs: number };
}

/* ───────────── Main app DTOs ───────────── */

/** What the authorisation screen shows before the user approves. */
export interface AuthorizeInfo {
  application: { id: string; name: string; description: string; iconUrl: string | null; owner: UserSummary; privacyPolicyUrl: string; termsUrl: string; createdAt: string };
  bot: { userId: string; username: string; displayName: string; avatarUrl: string | null } | null;
  scopes: OAuthScope[];
  /** Requested bot permissions (bot scope only). */
  permissions: number;
  /** Validated redirect URI, or null for bot-only installs. */
  redirectUri: string | null;
  state: string | null;
  /** Communities the user can install into (bot scope only). */
  communities: { id: string; name: string; slug: string; iconUrl: string | null; installed: boolean; canInstall: boolean; myPermissions: number }[];
  /** Pre-selected community from the link, if any. */
  communityId: string | null;
}

export interface AuthorizeResult {
  /** Where to send the browser next (the app's redirect URI with code or error). */
  redirectTo: string | null;
  /** Set when a bot was installed. */
  installed: { communityId: string; communitySlug: string; communityName: string } | null;
}

export interface AuthorizedApp {
  application: { id: string; name: string; iconUrl: string | null; description: string };
  scopes: OAuthScope[];
  authorizedAt: string;
}

export interface CommunityIntegration {
  application: { id: string; name: string; iconUrl: string | null; description: string };
  bot: UserSummary;
  permissions: number;
  installedBy: UserSummary | null;
  installedAt: string;
  roleId: string | null;
  commands: { name: string; description: string }[];
}

/** A slash command available in a community, for the composer. */
export interface AvailableCommand {
  applicationId: string;
  applicationName: string;
  bot: UserSummary;
  name: string;
  description: string;
  options: CommandOption[];
}

export const invokeCommandSchema = z.object({
  applicationId: z.string().uuid(),
  name: z.string().min(1).max(32),
  options: z.record(z.string(), z.union([z.string().max(1000), z.number(), z.boolean()])).default({}),
});

export const authorizeRequestSchema = z.object({
  client_id: z.string().uuid("Unknown application"),
  scope: z.string().max(200).default(""),
  response_type: z.literal("code").optional(),
  redirect_uri: z.string().max(500).optional(),
  state: z.string().max(500).optional(),
  permissions: permissionField.optional(),
  community_id: z.string().uuid().optional(),
  code_challenge: z.string().min(43).max(128).optional(),
  code_challenge_method: z.enum(["S256", "plain"]).optional(),
});
export type AuthorizeRequest = z.infer<typeof authorizeRequestSchema>;

export const authorizeDecisionSchema = authorizeRequestSchema.extend({
  approve: z.boolean(),
  /** Bot scope: the community chosen on the authorisation screen. */
  communityId: z.string().uuid().optional(),
  /** Bot scope: the permissions left ticked. Must be a subset of those requested. */
  grantedPermissions: permissionField.optional(),
});

/** Build an install/authorise URL. Used by the portal's URL generator and the SDK. */
export function buildAuthorizeUrl(
  appOrigin: string,
  opts: { clientId: string; scopes: OAuthScope[]; permissions?: number; redirectUri?: string; state?: string; communityId?: string; codeChallenge?: string },
): string {
  const u = new URL("/oauth2/authorize", appOrigin);
  u.searchParams.set("client_id", opts.clientId);
  u.searchParams.set("scope", opts.scopes.join(" "));
  if (opts.scopes.includes("bot") && opts.permissions !== undefined) u.searchParams.set("permissions", String(opts.permissions >>> 0));
  if (opts.redirectUri) {
    u.searchParams.set("redirect_uri", opts.redirectUri);
    u.searchParams.set("response_type", "code");
  }
  if (opts.state) u.searchParams.set("state", opts.state);
  if (opts.communityId) u.searchParams.set("community_id", opts.communityId);
  if (opts.codeChallenge) {
    u.searchParams.set("code_challenge", opts.codeChallenge);
    u.searchParams.set("code_challenge_method", "S256");
  }
  return u.toString();
}

/** Public origins of every surface, served by the main app at /api/config. */
export interface PlatformOrigins {
  app: string;
  developers: string;
  api: string;
  docs: string;
  cdn: string;
}

/** Origins written into the docs and their code samples, swapped for the real ones when shown. */
export const PLACEHOLDER_ORIGINS: PlatformOrigins = {
  app: "https://app.example.com",
  developers: "https://platform.example.com",
  api: "https://api.example.com",
  docs: "https://docs.example.com",
  cdn: "https://cdn.example.com",
};

/** Replace placeholder origins in docs text with the real ones, then bare placeholder hosts ("api.example.com"). */
export function withOrigins(text: string, o: PlatformOrigins): string {
  const keys = Object.keys(PLACEHOLDER_ORIGINS) as (keyof PlatformOrigins)[];
  const host = (origin: string) => origin.replace(/^https?:\/\//, "");
  for (const k of keys) text = text.replaceAll(PLACEHOLDER_ORIGINS[k], o[k]);
  for (const k of keys) text = text.replaceAll(host(PLACEHOLDER_ORIGINS[k]), host(o[k]));
  return text;
}
